import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import SeoHead from "./SeoHead";
import {
  DEFAULT_LOCALE,
  getAlternateLanguageLinks,
  getLocaleFromPathname,
  buildLocalizedUrl,
  stripLocalePrefix,
} from "../utils/localeRouting";
import { PRIMARY_SITE_ORIGIN } from "../utils/seo";

const ROUTE_SEO = {
  "/about-us": {
    title: "LUKKEY About Us | Swiss Hardware Wallet Security",
    description:
      "Learn about LUKKEY, a Swiss hardware wallet brand focused on secure self-custody, offline private key protection, NFC backup, and open security design.",
    schemaType: "AboutPage",
  },
  "/privacy": {
    title: "LUKKEY Privacy Policy | Hardware Wallet Privacy",
    description:
      "Read how LUKKEY designs hardware wallet privacy around offline private key storage, local control, and secure self-custody workflows.",
    schemaType: "PrivacyPolicy",
  },
  "/terms-of-service": {
    title: "LUKKEY Terms of Service",
    description:
      "Review the terms that apply to LUKKEY products, services, website use, support, orders, and digital asset self-custody responsibilities.",
    schemaType: "WebPage",
  },
  "/warranty-policy": {
    title: "LUKKEY Warranty Policy | Hardware Wallet Support",
    description:
      "Review LUKKEY two-year limited hardware warranty coverage, Lukkey Care+ protection up to five years, service eligibility, and support request steps.",
    schemaType: "WebPage",
  },
  "/compliance": {
    title: "LUKKEY Compliance | FCC, EU DoC, and RoHS",
    description:
      "Review official LUKKEY product compliance information for Model No. L2026, including wireless specifications, FCC Part 15 notices, EU declaration, and RoHS compliance.",
    schemaType: "WebPage",
  },
  "/app": {
    title: "LUKKEY App Download | iOS, Android, and APK",
    description:
      "Download the official LUKKEY app for iOS and Android to manage compatible LUKKEY hardware wallet workflows.",
    schemaType: "MobileApplication",
  },
  "/support-request": {
    title: "LUKKEY Support Request | Hardware Wallet Help",
    description:
      "Contact LUKKEY support for hardware wallet setup, warranty service, app download help, delivery questions, and product assistance.",
    schemaType: "ContactPage",
  },
  "/social": {
    title: "LUKKEY Social | Official Social Channels",
    description:
      "Find official LUKKEY social channels for product updates, hardware wallet support, security news, and self-custody discussions.",
    schemaType: "CollectionPage",
  },
};

const SELF_MANAGED_SEO_PREFIXES = [
  "/help-center",
  "/shipping/page",
  "/media-kit",
  "/supported-crypto-assets",
  "/partners",
];

const SELF_MANAGED_SEO_PATHS = new Set([
  "/",
  "/lukkey",
  "/co-branding",
  "/affiliate/apply",
  "/cobranding/apply",
  "/co-branding/apply",
]);

const NO_INDEX_PREFIXES = [
  "/admin",
  "/audit",
  "/brc20",
  "/demo",
  "/design",
  "/3drender",
  "/finance",
  "/financial",
  "/issues",
  "/internal",
  "/jaime",
  "/lukkey-issues",
  "/pre",
  "/purchase/success",
  "/r2-files",
  "/scm",
  "/subscribe/confirmed",
  "/workspace",
];

function normalizeRoutePath(pathname = "/") {
  const stripped = stripLocalePrefix(pathname || "/").replace(/\/{2,}/g, "/");
  if (stripped !== "/" && stripped.endsWith("/")) {
    return stripped.slice(0, -1);
  }
  return stripped || "/";
}

function RouteSeoHead() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const routePath = normalizeRoutePath(location.pathname);
  const currentLocale =
    getLocaleFromPathname(location.pathname) ||
    i18n.resolvedLanguage ||
    i18n.language ||
    DEFAULT_LOCALE;

  if (NO_INDEX_PREFIXES.some((prefix) => routePath === prefix || routePath.startsWith(`${prefix}/`))) {
    return null;
  }

  if (
    SELF_MANAGED_SEO_PATHS.has(routePath) ||
    SELF_MANAGED_SEO_PREFIXES.some((prefix) => routePath === prefix || routePath.startsWith(`${prefix}/`))
  ) {
    return null;
  }

  const seo = ROUTE_SEO[routePath];
  if (!seo) return null;

  const localizedCanonicalUrl = buildLocalizedUrl(
    routePath,
    PRIMARY_SITE_ORIGIN,
    currentLocale,
  );
  const alternates = getAlternateLanguageLinks(routePath, PRIMARY_SITE_ORIGIN);
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": seo.schemaType || "WebPage",
      name: seo.title,
      description: seo.description,
      url: localizedCanonicalUrl,
      inLanguage: currentLocale,
      isPartOf: {
        "@type": "WebSite",
        "@id": `${PRIMARY_SITE_ORIGIN}/#website`,
        name: "LUKKEY",
        url: PRIMARY_SITE_ORIGIN,
      },
      publisher: {
        "@type": "Organization",
        "@id": `${PRIMARY_SITE_ORIGIN}/#organization`,
        name: "LUKKEY",
        url: PRIMARY_SITE_ORIGIN,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "LUKKEY",
          item: `${PRIMARY_SITE_ORIGIN}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: seo.title.replace(/\s*\|\s*.*$/, ""),
          item: localizedCanonicalUrl,
        },
      ],
    },
  ];

  return (
    <SeoHead
      lang={currentLocale}
      title={seo.title}
      description={seo.description}
      canonicalUrl={localizedCanonicalUrl}
      alternates={alternates}
      type="website"
      schema={schema}
    />
  );
}

export default RouteSeoHead;
